import Styles from './Preview.module.scss'
import { useState, useEffect, useRef } from 'react'
import { StoreSnapshot } from '@tldraw/tldraw'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { Ref } from 'components/DesignContentPreview/Types'
import { useExtractTextBoxes } from 'components/VisualStudioPlayground/VisualEditor/hooks/useExtractTextBoxes'
import { FontPairing } from 'constants/fontPairings'
import { observer } from 'mobx-react-lite'
import { InstagramStoryPreview } from 'components/Previews/InstagramStoryPreview/InstagramStoryPreview'
import { ExternalIntegrationEnum, InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import { BrandKit } from 'types'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import Color from 'colorjs.io'
import { Button } from 'blaze-ui/Button'
import { usePreviewState } from '../PreviewProvider/PreviewProvider'
import { Shuffle } from 'blaze-ui/icons'
import { reportError } from 'utils/error'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'

interface Props {
  colors?: string[]
  fontStyles?: FontPairing
  media?: IMediaFile
  brandKit?: BrandKit
  showShuffleButton?: boolean
  size?: 'normal' | 'small'
}

const DEFAULT_PALETTE = ['#000000', '#FFFFFF']

const buildMockIntegration = (name: string, avatarUrl: string): InstagramIntegration => {
  const now = new Date().toISOString()

  return {
    id: 1,
    provider: ExternalIntegrationEnum.Instagram,
    provider_id: 'preview-instagram',
    name,
    picture: avatarUrl,
    account_type: 'profile',
    created_at: now,
    updated_at: now,
    scopes: [],
    last_heartbeat_checked_at: now,
    last_disconnected_email_sent_at: null,
    disconnected_email_count: 0,
    is_active: true,
    type: 'ExternalIntegration',
    additional_data: {
      username: name,
      profile_picture_url: avatarUrl,
      id: 'preview',
    },
  }
}

export const Preview = observer(
  ({ colors = DEFAULT_PALETTE, fontStyles, media, brandKit, showShuffleButton = true, size = 'normal' }: Props) => {
    const { generatedContent, isGenerating } = usePreviewState()

    const [content, setContent] = useState<StoreSnapshot<any> | null>(generatedContent)
    const [editorRef, setEditorRef] = useState<Ref | null>(null)

    const appliedPaletteRef = useRef('')
    const appliedFontsRef = useRef('')

    const extractTextBoxes = useExtractTextBoxes()

    const applyPalette = async () => {
      if (!editorRef) return

      try {
        const isDefaultPalette =
          colors.length === DEFAULT_PALETTE.length && colors.every((c, i) => c === DEFAULT_PALETTE[i])
        const backgroundColor = isDefaultPalette ? new Color(colors[1] || '#FFFFFF') : undefined
        const newSnapshot = await editorRef.applyPalette(colors, backgroundColor)
        if (newSnapshot) setContent(newSnapshot)
      } catch (err) {
        reportError(err)
      }
    }

    const handleShuffle = () => applyPalette()

    useEffect(() => {
      if (generatedContent && !content) setContent(generatedContent)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatedContent])

    useEffect(() => {
      if (!editorRef) return
      const paletteKey = colors.join(',')
      if (appliedPaletteRef.current === paletteKey) return
      appliedPaletteRef.current = paletteKey
      const t = setTimeout(applyPalette, 100)
      return () => clearTimeout(t)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorRef, colors])

    useEffect(() => {
      if (!editorRef || !fontStyles) return
      const fontKey = JSON.stringify(fontStyles)
      if (appliedFontsRef.current === fontKey) return

      const applyFonts = async () => {
        try {
          const snap = await editorRef.getEditor().store.getSnapshot()
          if (!snap) return
          const boxes = extractTextBoxes(snap)
          if (!boxes.length) return
          const newSnap = await editorRef.applyTextStyles(fontStyles, boxes)
          if (newSnap) {
            setContent(newSnap)
            appliedFontsRef.current = fontKey
          }
        } catch (err) {
          reportError(err)
        }
      }

      const t = setTimeout(applyFonts, 100)
      return () => clearTimeout(t)
    }, [editorRef, fontStyles, extractTextBoxes])

    if (isGenerating) {
      return (
        <div className={Styles.loadingContainer} data-testid="preview-loading">
          <Text variant="placeholder">
            Generating brand kit preview
            <LoadingEllipses />
          </Text>
        </div>
      )
    }

    if (!content) return null

    const avatar = brandKit?.brand_visual_kit?.primary_logo?.url ?? 'https://via.placeholder.com/150'

    return (
      <div className={size === 'small' ? Styles.rootSmall : Styles.root} data-testid="preview-component">
        <div className={Styles.storyWrapper}>
          <InstagramStoryPreview
            integration={buildMockIntegration(brandKit?.name ?? 'Instagram', avatar)}
            customPreviewContent={<DesignContentPreview content={content} ref={setEditorRef} />}
          />
        </div>
        {showShuffleButton && (
          <div className={Styles.shuffleButton}>
            <Button size="sm" onPress={handleShuffle} aria-label="Shuffle colors" variant="editor" frontIcon={Shuffle}>
              Shuffle Colors
            </Button>
          </div>
        )}
      </div>
    )
  },
)
